import { useEffect, useState, type ReactNode } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  groupMaxRounds,
  interleaveRounds,
  memberLetter,
  numericFieldErrorText,
  VALUELESS_KINDS,
  type BlockMember,
  type IntensityKind,
  type UnitsPreference,
} from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import { Chip } from "../primitives/Chip";
import { InfoPill, InfoPillBody } from "../primitives/InfoPill";
import { PauseExplainer } from "../PauseExplainer";
import { RevealChip, useRevealChip } from "./useRevealChip";
import { Select } from "../ProgramEditor/Select";
import type { EditorStyles } from "../ProgramEditor/styles";
import type { UiTheme } from "../theme";
import { useKindLabels } from "./useKindLabels";
import type { ComposerStyles } from "./composerStyles";
import { ComposerMemberHead } from "./ComposerMemberHead";
import { ComposerSetRow } from "./ComposerSetRow";
import { valueFieldKind } from "./composerSetTableShared";
import type { FocusChainFieldProps } from "../primitives/useFocusChain";

// The form's metric table (§5.2 — the logger idiom, all the way now): ONE
// table for the whole block, solo or superset. Member head rows (A/B frame,
// name, kind selector, 📝 exercise note, ⇄/✕) on top, then interleaved set
// rows from shared interleaveRounds — 1A·1B·2A·2B, exactly like the logger's
// GroupSetRows. "+ Add" appends a round to EVERY member; the per-row "–"
// drops the whole round across members (shared removeRoundFromSets safety
// rules upstream in useComposer). One reps hint, one validation line.
//
// COLUMN SIZING diverges from the logger's set table on purpose (PE-17): the
// logger fixes its data columns and flexes a spacer, but this editor lives in
// a narrower card (edit-in-place) with an extra conditional note column, so
// here the DATA columns (reps/value) flex and only the fixed-box action
// columns stay rigid — that's what keeps the row inside the card. Don't
// "restore parity" by re-fixing reps/value; it reintroduces the overflow.
//
// NOTES ARE PER-EXERCISE (Antenore 2026-06-12): the 📝 in the member head
// edits slot `instructions`, which programResolution already surfaces in the
// logger as the entry note. The per-set note column is LEGACY-ONLY — it
// renders only when an existing row carries a note (imported/old programs),
// so old data stays reachable but no new per-set notes can be authored.
// #336: the phone editor's round view passes `noteToggle`, a «Note» toggle
// chip that shows the column on every set of the round, and hides it again
// while no set has a note.

const KIND_ORDER: IntensityKind[] = [
  "none",
  "absolute",
  "percent-of-max",
  "history",
  "rpe",
  "rir",
];

export function ComposerSetTable({
  members,
  grouped,
  onRemoveMember,
  onReplaceMember,
  onUpdateRow,
  onSetKind,
  onSetInstructions,
  onAddRound,
  onRemoveRound,
  cellUnit,
  styles,
  selectStyles,
  theme,
  axisSlot,
  hideMemberHeads = false,
  fieldProps,
  noteToggle = false,
  rangeToggle = false,
  amrapStop = false,
  pauseToggle = false,
}: {
  members: BlockMember[];
  grouped: boolean;
  onRemoveMember: (memberIdx: number) => void;
  onReplaceMember: (memberIdx: number) => void;
  onUpdateRow: (
    memberIdx: number,
    rowIdx: number,
    patch: Partial<BlockMember["setRows"][number]>,
  ) => void;
  onSetKind: (memberIdx: number, kind: IntensityKind) => void;
  onSetInstructions: (memberIdx: number, text: string) => void;
  onAddRound: () => void;
  onRemoveRound: (roundIdx: number) => void;
  // Unit the absolute-weight cells are expressed in — drives the column
  // header so the label can never contradict the numbers below it.
  // ⛔ Required (CN-26 wave 3): see useKindLabels.
  cellUnit: UnitsPreference;
  styles: ComposerStyles;
  selectStyles: EditorStyles;
  theme: UiTheme;
  // #336, the phone editor's round view. `axisSlot`: the load axis is chosen
  // above the table (LoadAxisSelector), so the head names it instead of
  // offering the Select. `hideMemberHeads`: the host already shows the
  // exercise and its actions. `fieldProps`: the keyboard's focus chain.
  axisSlot?: ReactNode;
  hideMemberHeads?: boolean;
  fieldProps?: (rowIdx: number, field: "reps" | "value") => FocusChainFieldProps;
  noteToggle?: boolean;
  /** #336 S3, the phone editor only: a «Range» chip reveals each set's rep
   *  range, like «Time». The wide editor locks a slot with a range (Q8). */
  rangeToggle?: boolean;
  /** #355, the v3 editor only: the stop rule under each AMRAP set. */
  amrapStop?: boolean;
  /** HI-8, the v3 editor only: a «Pause» chip reveals each set's pause
   *  inside the rep, like «Range». */
  pauseToggle?: boolean;
}) {
  const { t } = useTranslation();
  const kindLabels = useKindLabels(cellUnit);
  const [noteOpen, setNoteOpen] = useState<string | null>(null);
  const [instrOpen, setInstrOpen] = useState<number | null>(null);
  // The "5+" explanation, revealed by the `?` in the REPS header (LG-23).
  // Closed by default and NOT persisted: it is a reminder you open when you
  // need it, not a setting.
  const [repsHelpOpen, setRepsHelpOpen] = useState(false);
  const [notesToggled, setNotesToggled] = useState(false);
  const [pauseHelpOpen, setPauseHelpOpen] = useState(false);
  // HI-8: the one set whose pause is open, by cell key like `noteOpen`.
  const [pauseEditing, setPauseEditing] = useState<string | null>(null);
  // Loading a different block into the form swaps the members wholesale but
  // does not remount the table — open editors would point at the wrong
  // member (review MINOR-2). Keyed on the exercise-id signature, NOT the
  // members array identity: every keystroke produces a fresh array and
  // would close the editor mid-typing.
  const membersKey = members.map((m) => m.exerciseId).join("|");
  useEffect(() => {
    setNoteOpen(null);
    setInstrOpen(null);
    setPauseEditing(null);
  }, [membersKey]);

  const counts = members.map((m) => m.setRows.length);
  const maxRounds = groupMaxRounds(counts);

  // Legacy per-set notes: the column exists only while some row still
  // carries one (or its editor is open mid-erase).
  const hasNotes = members.some((m) => m.setRows.some((r) => r.note.trim() !== ""));
  const legacyNotes = notesToggled || noteOpen !== null || hasNotes;

  // The reveal chips (useRevealChip): open by the author's tap OR because the
  // block already carries the value. Opening a stored HIIT block must SHOW its
  // bouts (HI-1); a stored range (#336 S3) or pause (HI-8) the same.
  const anyRow = (has: (r: BlockMember["setRows"][number]) => boolean) =>
    members.some((m) => m.setRows.some(has));
  const time = useRevealChip(true, anyRow((r) => r.workKind !== undefined || !!r.restSec));
  const range = useRevealChip(rangeToggle, anyRow((r) => !!r.repsMin || !!r.repsMax));
  const pause = useRevealChip(pauseToggle, anyRow((r) => r.pausePhase !== undefined));
  const timeOpen = time.open;
  const rangeOpen = range.open;
  const pauseOpen = pause.open;

  // Header kind: the first member's dominant kind drives the column header
  // (the logger's first-member-shape rule); a row whose kind diverges shows
  // its own label inline/as placeholder. Solo keeps the header AS the kind
  // selector (§5.2); grouped members each get a selector in their head row.
  const headerKind = members[0]?.setRows[0]?.intensityKind ?? "percent-of-max";

  // First invalid cell's message wins (never colour-only: cell border + this
  // line); ordinal-prefixed when grouped so it names the member.
  let validation: string | null = null;
  for (const [idx, member] of members.entries()) {
    for (const row of member.setRows) {
      const msg =
        numericFieldErrorText("prescribedReps", row.reps, t) ??
        (VALUELESS_KINDS.has(row.intensityKind)
          ? null
          : numericFieldErrorText(
              valueFieldKind(row.intensityKind),
              row.intensityValue,
              t,
            ));
      // ⛔ The seconds cells deliberately do NOT report here. This line is
      // first-error-wins for the whole table: folding two more fields per
      // row into it would leave the later ones as a red border with no text
      // anywhere. They carry their own message, beside the field.
      if (msg) {
        validation = grouped ? `${memberLetter(idx)} · ${msg}` : msg;
        break;
      }
    }
    if (validation) break;
  }

  const kindSelect = (memberIdx: number, kind: IntensityKind) => (
    <Select
      value={kind}
      options={KIND_ORDER.map((k) => ({ value: k, label: kindLabels[k] }))}
      onChange={(v) => onSetKind(memberIdx, v as IntensityKind)}
      styles={selectStyles}
      accessibilityLabel={t("program.intensityKind", {
        defaultValue: "Intensity",
      })}
    />
  );

  return (
    <View style={[styles.memberCard, grouped && styles.memberCardGrouped]}>
      {axisSlot}
      {!hideMemberHeads && members.map((member, idx) => (
        <ComposerMemberHead
          key={`${member.exerciseId}-${idx}`}
          member={member}
          idx={idx}
          grouped={grouped}
          headerKind={headerKind}
          instructionsOpen={instrOpen === idx}
          onToggleInstructions={() =>
            setInstrOpen((cur) => (cur === idx ? null : idx))
          }
          onSetInstructions={(v) => onSetInstructions(idx, v)}
          onReplaceMember={() => onReplaceMember(idx)}
          onRemoveMember={() => onRemoveMember(idx)}
          renderKindSelect={kindSelect}
          styles={styles}
          theme={theme}
        />
      ))}

      <View style={styles.tableHeadRow}>
        {/* program.setCol, not program.set: the column is 36 wide and «ПОДХОД» broke mid-word in it. */}
        <Text style={[styles.headText, styles.colSet]}>
          {t("program.setCol", { defaultValue: "Set" }).toUpperCase()}
        </Text>
        <Text style={[styles.headText, styles.colReps]}>
          {t("program.reps", { defaultValue: "Reps" }).toUpperCase()}
        </Text>
        <View style={styles.colValue}>
          {grouped || axisSlot ? (
            <Text style={styles.headText}>{kindLabels[headerKind]}</Text>
          ) : (
            kindSelect(0, headerKind)
          )}
        </View>
        {legacyNotes ? (
          <View style={styles.colNote}>
            <Icon name="note" size={styles.headText.fontSize} color={styles.headText.color} />
          </View>
        ) : null}
        {/* The `?` that explains the "5+" AMRAP syntax replaces the hint line
            that used to stand under EVERY exercise editor, permanently
            (LG-23, inbox 3d88179b). It rides the header's FIXED 36-box — the
            one the row-remove column reserves and the header leaves empty —
            NOT the reps column: reps/value FLEX (PE-17), so a fixed 28 pill
            inside them squeezes the value select until it wraps to one letter
            per line at 280dp. Verified on the headless test device, which is how that first
            attempt was caught. */}
        <View style={styles.colRowRemove}>
          <InfoPill
            theme={theme}
            open={repsHelpOpen}
            onToggle={() => setRepsHelpOpen((v) => !v)}
            a11yLabel={t("composer.repsHintA11y", {
              defaultValue: "How reps are written",
            })}
          />
        </View>
      </View>

      {interleaveRounds(counts).map(({ memberIdx, roundIdx, label }) => {
        // Both assertions hold by construction: interleaveRounds only emits
        // cells with roundIdx < counts[memberIdx], and counts is derived
        // from these same members above.
        const member = members[memberIdx]!;
        const row = member.setRows[roundIdx]!;
        const cellKey = `${memberIdx}-${roundIdx}`;
        return (
          <ComposerSetRow
            key={cellKey}
            row={row}
            label={label}
            headerKind={headerKind}
            kindLabels={kindLabels}
            legacyNotes={legacyNotes}
            noteEveryRow={noteToggle && legacyNotes}
            showRemoveRound={maxRounds > 1}
            noteOpen={noteOpen === cellKey}
            timeOpen={timeOpen}
            rangeOpen={rangeOpen}
            amrapStopLine={amrapStop}
            pauseLine={pauseOpen}
            pauseExpanded={pauseEditing === cellKey}
            onTogglePause={() => setPauseEditing((cur) => (cur === cellKey ? null : cellKey))}
            onToggleNote={() =>
              setNoteOpen((cur) => (cur === cellKey ? null : cellKey))
            }
            onUpdateRow={(patch) => onUpdateRow(memberIdx, roundIdx, patch)}
            onRemoveRound={() => onRemoveRound(roundIdx)}
            styles={styles}
            theme={theme}
            focus={
              fieldProps
                ? { reps: fieldProps(roundIdx, "reps"), value: fieldProps(roundIdx, "value") }
                : undefined
            }
          />
        );
      })}

      {validation ? (
        <Text accessibilityLiveRegion="polite" style={styles.validationText}>{validation}</Text>
      ) : null}

      {/* The AMRAP syntax must be discoverable, not folklore (Antenore
          2026-06-11) — but discoverable is the `?` in the REPS header, not a
          line that stands there forever under every exercise (LG-23). */}
      {repsHelpOpen ? (
        <InfoPillBody
          theme={theme}
          text={t("composer.repsHint", {
            defaultValue:
              "Reps “5+” = AMRAP: at least 5, then as many as possible.",
          })}
        />
      ) : null}

      <View style={styles.footerRow}>
        <Pressable
          accessibilityRole="button"
          onPress={onAddRound}
          style={styles.addSetBtn}
        >
          <View style={styles.addSetRow}>
            <Icon
              name="plus"
              size={styles.addSetText.fontSize}
              color={styles.addSetText.color}
            />
            <Text style={styles.addSetText}>
              {grouped
                ? t("mobile.logger.addRound", { defaultValue: "Add round" })
                : t("program.addSet", { defaultValue: "Add set" })}
            </Text>
          </View>
        </Pressable>
        {/* ONE control for the whole table (HI-1). It reveals the per-set
            time lines instead of adding a column to every row — this table's
            columns flex into a narrow card and its header warns what a fixed
            box does to them (PE-17). It disappears once the block carries a
            prescription: from then on `timeOpen` is true because the data
            says so, and a toggle that cannot turn anything off is a lie. */}
        {timeOpen ? null : (
          <RevealChip
            theme={theme}
            label={t("program.prescribeTime", { defaultValue: "Time" })}
            a11yLabel={t("program.prescribeTimeA11y", {
              defaultValue: "Prescribe work and rest in seconds",
            })}
            onReveal={time.reveal}
          />
        )}
        {rangeToggle && !rangeOpen ? (
          <RevealChip
            theme={theme}
            label={t("program.repsRange", { defaultValue: "Range" })}
            a11yLabel={t("program.prescribeRangeA11y", {
              defaultValue: "Prescribe a rep range on the sets",
            })}
            onReveal={range.reveal}
          />
        ) : null}
        {pauseToggle && !pauseOpen ? (
          <RevealChip
            theme={theme}
            label={t("program.pause.title", { defaultValue: "Pause" })}
            a11yLabel={t("program.pause.chipA11y", {
              defaultValue: "Prescribe a pause inside the reps",
            })}
            // The chip is gone and focus with it: say what appeared.
            announce={t("program.pause.revealed", {
              defaultValue: "Pause added under each set",
            })}
            onReveal={pause.reveal}
          />
        ) : null}
        {noteToggle ? (
          <Chip
            theme={theme}
            dense
            shape="pill"
            label={t("composer.setNoteChip", { defaultValue: "Note" })}
            selected={legacyNotes}
            // A set with a note keeps the column: the tap does nothing and the
            // hint says why. Not `disabled`: its 0.5 opacity on the selected
            // brand fill leaves the white label unreadable.
            onPress={() => {
              if (hasNotes) return;
              setNotesToggled((v) => !v);
              setNoteOpen(null);
            }}
            accessibilityLabel={t("composer.setNoteChipA11y", {
              defaultValue: "Write a note on a set of this round",
            })}
            accessibilityHint={
              hasNotes
                ? t("composer.setNoteChipLocked", { defaultValue: "Shown while a set has a note" })
                : undefined
            }
          />
        ) : null}
        {pauseOpen ? (
          // Last in the footer, so the body below follows it in reading order.
          // The three phase names arrive with their meaning (§10.6).
          <InfoPill
            theme={theme}
            open={pauseHelpOpen}
            onToggle={() => setPauseHelpOpen((v) => !v)}
            a11yLabel={t("program.pause.explainerA11y", {
              defaultValue: "What eccentric, concentric and isometric mean",
            })}
          />
        ) : null}
      </View>
      {pauseOpen && pauseHelpOpen ? (
        <InfoPillBody
          theme={theme}
          title={t("program.pause.explainerTitle", { defaultValue: "Pause inside a rep" })}
        >
          <PauseExplainer theme={theme} />
        </InfoPillBody>
      ) : null}
    </View>
  );
}
